import { Link as TipTapLink } from "@tiptap/extension-link"
import { EditorContent, useEditor } from "@tiptap/react"
import { StarterKit } from "@tiptap/starter-kit"
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List as BulletListIcon,
  ListOrdered as OrderedListIcon,
} from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { ProseMirrorDoc } from "@/types/missions"

/**
 * RichTextEditor — TipTap wrapper for the OKS-29 Mission brief field.
 *
 * Designed as a **reusable primitive** so OKS-45's AI-brief generator
 * can re-mount it with a pre-filled `defaultValue` without re-implementing
 * the editor surface.
 *
 * Server-side allow-list (binding):
 *   - nodes: doc, paragraph, bulletList, orderedList, listItem, hardBreak, text
 *   - marks: bold, italic, link  (link `href` MUST be `https://`)
 *
 * The editor enforces the same allow-list as UX (toolbar exposes only
 * the allowed marks; link prompt rejects non-https schemes inline) but
 * the **server is the source of truth** — `Missions::BriefSchemaValidator`
 * walks the JSON on every save and emits structured codes the FE
 * surfaces via the form's render-prop `errors` map.
 *
 * Wire shape: a hidden `<input type="hidden" name={name}>` carries
 * `JSON.stringify(doc)` so the multipart-form path (Active Storage
 * direct upload) preserves the brief intact. The controller's
 * `decode_brief_param!` decodes the JSON before assignment.
 *
 * NO `react-hook-form`, NO `zod` — Crew rules. State is local; the
 * hidden-input wire is the only Inertia-visible surface.
 *
 * A11y:
 *   - Toolbar buttons are real `<button type="button">` with `aria-label`
 *     and `aria-pressed` reflecting active marks.
 *   - The editor surface carries `role="textbox"` + `aria-multiline="true"`
 *     via TipTap's defaults; we add `aria-label` so the SR user knows
 *     what they're typing into.
 *   - Keyboard: TipTap supports the standard rich-text shortcuts
 *     (Ctrl/Cmd+B, +I) out of the box.
 */
interface RichTextEditorProps {
  /** Hidden input `name` — typically `"mission[brief]"`. */
  name: string
  /** Initial document (Edit page) or `null` for an empty editor (New). */
  defaultValue?: ProseMirrorDoc | Record<string, unknown> | null
  /** When false, renders read-only (Show page). */
  editable?: boolean
  /** Optional placeholder text (defaults to i18n `missions.form.brief.placeholder`). */
  placeholder?: string
  /** SR label — defaults to i18n `missions.form.brief.label`. */
  ariaLabel?: string
  /** Callback fires on every doc change — useful for OKS-45's AI panel. */
  onChange?: (doc: ProseMirrorDoc) => void
  /** Forwarded to the editor surface. */
  className?: string
  /** id forwarded to the hidden input — for aria-describedby pairing. */
  id?: string
  /** Marks the input invalid for SR + visual styling. */
  invalid?: boolean
  /** Space-separated id list for aria-describedby on the editor surface. */
  describedBy?: string
  /** Test ID for the editor wrapper (Playwright + Vitest hooks). */
  testId?: string
}

const EMPTY_DOC: ProseMirrorDoc = { type: "doc" }

export function RichTextEditor({
  name,
  defaultValue,
  editable = true,
  placeholder,
  ariaLabel,
  onChange,
  className,
  id,
  invalid,
  describedBy,
  testId = "rich-text-editor",
}: RichTextEditorProps) {
  const { t } = useTranslation()
  const initialContent = useMemo(
    () =>
      defaultValue && typeof defaultValue === "object"
        ? defaultValue
        : EMPTY_DOC,
    [defaultValue],
  )

  // Hidden-input value — kept in sync with the editor's JSON via
  // onUpdate. Reading editor.getJSON() directly inside render would force
  // a re-render every keystroke; this state is the single source of truth
  // for the wire shape and only updates when the doc actually changes.
  const [serialized, setSerialized] = useState<string>(() =>
    JSON.stringify(initialContent),
  )

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        // Allow-list match — disable everything outside the spec.
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        strike: false,
        horizontalRule: false,
      }),
      TipTapLink.configure({
        // Server enforces https-only via invalid_link_scheme; we mirror
        // the allow-list here as UX so the toolbar's link prompt rejects
        // bad schemes inline rather than waiting for a server round-trip.
        protocols: ["https"],
        autolink: false,
        openOnClick: false,
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        "aria-label": ariaLabel ?? t("missions.form.brief.label"),
        "aria-multiline": "true",
        "aria-invalid": invalid ? "true" : "false",
        ...(describedBy ? { "aria-describedby": describedBy } : {}),
        class: cn(
          // Editable surface uses a tonal border + inset padding — matches
          // the shadcn Input visual weight without inheriting its h-9.
          "bg-background min-h-32 w-full rounded-md border px-3 py-2 text-sm",
          "ring-offset-background focus-within:ring-ring focus-within:ring-2",
          "prose prose-sm max-w-none focus-within:ring-offset-2",
          invalid ? "border-destructive" : "border-input",
          editable ? "" : "bg-muted text-muted-foreground",
        ),
      },
    },
    onUpdate({ editor: e }) {
      const json = e.getJSON() as ProseMirrorDoc
      setSerialized(JSON.stringify(json))
      onChange?.(json)
    },
  })

  const handleBold = useCallback(
    () => editor?.chain().focus().toggleBold().run(),
    [editor],
  )
  const handleItalic = useCallback(
    () => editor?.chain().focus().toggleItalic().run(),
    [editor],
  )
  const handleBulletList = useCallback(
    () => editor?.chain().focus().toggleBulletList().run(),
    [editor],
  )
  const handleOrderedList = useCallback(
    () => editor?.chain().focus().toggleOrderedList().run(),
    [editor],
  )
  const handleLink = useCallback(() => {
    if (!editor) return

    // If a link is already active under the selection, unset it. Otherwise
    // prompt for a URL — only `https://` is accepted (server lock; we
    // mirror as UX). prompt() returning null = user cancelled; empty
    // string = user pressed OK without typing anything (treated as
    // cancel).
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run()
      return
    }

    const raw = window.prompt(t("missions.form.brief.placeholder"), "https://")
    if (!raw) return

    const url = raw.trim()
    if (!url.startsWith("https://")) return // Mirrors invalid_link_scheme.

    editor.chain().focus().setLink({ href: url }).run()
  }, [editor, t])

  if (!editor) return null

  return (
    <div
      data-testid={testId}
      data-rt-editor-root=""
      className={cn("flex flex-col gap-2", className)}
    >
      {editable && (
        <div
          role="toolbar"
          aria-label={t("missions.form.brief.label")}
          className="flex flex-wrap gap-1"
        >
          <ToolbarButton
            label={t("missions.form.brief.label") + " — bold"}
            pressed={editor.isActive("bold")}
            onClick={handleBold}
            testId="rich-text-editor-bold"
          >
            <BoldIcon className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label={t("missions.form.brief.label") + " — italic"}
            pressed={editor.isActive("italic")}
            onClick={handleItalic}
            testId="rich-text-editor-italic"
          >
            <ItalicIcon className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label={t("missions.form.brief.label") + " — bullet list"}
            pressed={editor.isActive("bulletList")}
            onClick={handleBulletList}
            testId="rich-text-editor-bulletList"
          >
            <BulletListIcon className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label={t("missions.form.brief.label") + " — ordered list"}
            pressed={editor.isActive("orderedList")}
            onClick={handleOrderedList}
            testId="rich-text-editor-orderedList"
          >
            <OrderedListIcon className="size-4" aria-hidden="true" />
          </ToolbarButton>
          <ToolbarButton
            label={t("missions.form.brief.label") + " — link"}
            pressed={editor.isActive("link")}
            onClick={handleLink}
            testId="rich-text-editor-link"
          >
            <LinkIcon className="size-4" aria-hidden="true" />
          </ToolbarButton>
        </div>
      )}

      <EditorContent editor={editor} placeholder={placeholder} />

      {/*
        Hidden wire — `JSON.stringify` of the current doc. The controller's
        `decode_brief_param!` decodes back to a Hash before validation.
      */}
      <input type="hidden" id={id} name={name} value={serialized} />
    </div>
  )
}

interface ToolbarButtonProps {
  label: string
  pressed: boolean
  onClick: () => void
  children: React.ReactNode
  testId: string
}

function ToolbarButton({
  label,
  pressed,
  onClick,
  children,
  testId,
}: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant={pressed ? "secondary" : "ghost"}
      size="sm"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed ? "true" : "false"}
      data-testid={testId}
      className="size-8 p-0"
    >
      {children}
    </Button>
  )
}
